import React, { useState } from 'react';
import { storage } from '../../../../firebase/clientApp';
import { ref, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import ProgressBar from '../../ProgressBar';
import IconButton from '../../buttons/IconButton';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../../firebase/clientApp';
import classNames from '../../../utility/classNames';
import { Icon } from '../../../components';

function FileUpload({
  label = 'image',
  path = '',
  handleFinish,
  skipUploadStep = false,
  type = 'gallery',
}) {
  const [progress, setProgress] = useState({});
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const storageRef = ref(storage);
  const handleChange = (e) => {
    if (e.target.files.length) {
      setFiles([...files, ...Object.values(e.target.files)]);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.items) {
      [...e.dataTransfer.items].forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (
            ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(
              file.type
            )
          ) {
            if (type === 'gallery' || type === 'property') {
              setFiles((files) => [...files, file]);
            } else {
              setFiles([file]);
            }
          }
        }
      });
    }
  };

  const handleDragover = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleFileRemove = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const overallProgress =
    Object.values(progress).reduce((acc, curr) => {
      if (acc === -1 && curr !== -1) {
        return curr + 1;
      }
      return curr === -1 ? acc : acc + curr;
    }, -1) / (Object.keys(progress).length || 1);

  const handleUpload = async () => {
    for (let i = 0; i < files.length; i++) {
      await handleFileUpload(files[i], i, i === files.length - 1);
    }
  };

  const handleFileUpload = async (file, index, last) => {
    setProgress({ ...progress, [index]: 0 });

    // Create a new file name
    const fileNameArray = file.name.split('.');
    fileNameArray[0] = `${fileNameArray[0]}-${Date.now()}`;

    // This can change to switch statement if we add more types
    const newName = type === 'logo' ? 'logo.png' : fileNameArray.join('.');

    // Create a reference
    const imagesRef = ref(storageRef, `${path}/${newName}`);

    // Create file metadata for the content type
    const metadata = {
      contentType: file?.type,
    };

    try {
      const uploadTask = uploadBytesResumable(imagesRef, file, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const currentProgress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setProgress({ ...progress, [index]: currentProgress });
        },
        (error) => {
          console.error(error);
          logEvent(analytics, 'exception', {
            content_type: type,
            description: `Error uploading file: ${error.code || error}`,
            fatal: true,
          });
          toast.error('Error uploading file', { position: 'top-right' });
          setProgress({ ...progress, [index]: -1 });
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((url) => handleFinish(newName, url, last))
            .catch((err) => console.error(err))
            .finally(() => {
              setProgress({ ...progress, [index]: -1 });
            });
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {/* {files && ( */}
      <>
        <h4 className="text-md font-medium text-black mt-4 mb-4">
          Files to Upload:
        </h4>
        <li
          className={classNames(
            'list-none p-2 rounded-md max-w-full min-w-[70%] max-h-56 shadow-inner-lg overflow-y-auto',
            dragging && 'border-2 border-dashed border-primary-700'
          )}
          onDrop={handleFileDrop}
          onDragOver={handleDragover}
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
        >
          {files.length ? (
            files.map((file, index) => (
              <ul
                key={file.name}
                className={'p-1 flex justify-between items-center'}
              >
                <div>
                  {file.name}{' '}
                  <span className="text-neutral-500">
                    ({Math.round((file.size / 1000) * 100) / 100} KB)
                  </span>
                </div>
                <button type="button" onClick={() => handleFileRemove(index)}>
                  <Icon icon="FiX" className="text-neutral-500"></Icon>
                </button>
              </ul>
            ))
          ) : (
            <>
              <div className="hidden sm:flex items-center justify-center h-48">
                <p className="w-2/3 text-neutral-500 text-base">
                  No files selected. Use button below or drag and drop file into
                  this area.
                </p>
              </div>
              <div className="flex sm:hidden items-center justify-center h-24">
                <p className="w-1/3 text-neutral-500 text-base">
                  No files selected.
                </p>
              </div>
            </>
          )}
        </li>
      </>
      {/* )} */}
      <div className="mt-1 mb-2 flex flex-col flex-wrap justify-center items-center sm:justify-start">
        <div className="mt-6 flex flex-wrap gap-4 justify-center items-center sm:justify-start">
          <div className="relative flex items-center rounded focus:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-700">
            <label
              htmlFor={label}
              className="inline-flex items-center px-3 py-[6px] border border-transparent shadow-sm text-base font-medium rounded text-white bg-primary-700 hover:bg-primary-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {type === 'gallery' || type === 'property'
                ? `Choose File(s)`
                : `Choose File`}
              <input
                type="file"
                multiple={type === 'gallery'}
                accept={'image/png, image/jpeg, image/jpg, image/svg+xml'}
                id={label}
                name={label}
                className="sr-only"
                onChange={handleChange}
              />
            </label>
          </div>
          {skipUploadStep && (
            <IconButton
              text="Skip This Step"
              icon="FiArrowRight"
              type="button"
              style="secondary"
              handleClick={() => skipUploadStep()}
            />
          )}
          <IconButton
            text={type === 'property' ? 'Upload and Finish' : 'Upload'}
            icon="upload"
            type="button"
            disabled={!files.length}
            style="outline"
            handleClick={() => handleUpload()}
          />
        </div>
      </div>

      <ProgressBar progress={overallProgress} />
    </>
  );
}

export default FileUpload;
